/**
 * 코멘트 영역 행 타입 — 일반 코멘트('comment')와 결재 액션 로그('log')가 한 리스트에 혼합 렌더.
 */

import type { RemoteFile } from '@/components/file-thumbnail/utils';

type Lang = 'ko' | 'en' | 'zh';

/** CommentItem 이 기대하는 단순 User 형태 */
export type CommentUser = {
  passportId: string;
  name: string;
  nickName?: string;
  divisionName?: string;
  departmentName?: string;
  positionName: string;
  profileImageUrl: string | null;
};

export type ApprovalLogAction =
  | 'create'
  | 'confirm'
  | 'reject'
  | 'return'
  /** 기안자의 반려요청 — 결재 상태를 바꾸지 않는 유일한 액션 (PORTAL-14138). */
  | 'return_request'
  | 'withdraw'
  | 'hold'
  | 'temporary'
  /** 근태 자가결재 품의 취소 */
  | 'cancel';

export type ApprovalLogTone = 'default' | 'confirm' | 'reject' | 'hold';

/** 결재 액션 로그 행 — content 없으면 본문 줄 생략. 삭제된 행은 v2 응답에서 제외돼 내려오지 않음. */
export type ApprovalLogItem = {
  type: 'log';
  id: string;
  action: ApprovalLogAction;
  content: string | null;
  createdAt: string;
  createdBy: CommentUser;
};

/** 일반 코멘트 행 — 포탈 공용 CommentItem 의 Comment 타입과 호환 필요. */
export type ApprovalCommentItem = {
  type: 'comment';
  id: string;
  content: string;
  attachments: RemoteFile[];
  createdBy: CommentUser;
  createdAt: string;
  updatedAt: string;
  isUpdated: boolean;
  isDeleted: boolean;
  isDeleteEnable: boolean;
  feedback: {
    agree: unknown[];
    agreeCount: number;
    disagree: unknown[];
    disagreeCount: number;
    recommend: { name: string; positionName: string }[];
    recommendCount: number;
    isAgree: boolean;
    isDisagree: boolean;
    isRecommend: boolean;
  };
  contentI18n?: Record<Lang, string | null>;
};

export type ApprovalCommentRow = ApprovalCommentItem | ApprovalLogItem;

/** 정렬 토글 — 최신순/오래된순. localStorage 키 `commentSortOrder` 로 보존(2/N). */
export type CommentSortOrder = 'desc' | 'asc';

/** localStorage 키 (2/N wiring) */
export const COMMENT_SORT_LS_KEY = 'commentSortOrder';
export const commentDraftLsKey = (documentId: string) =>
  `commentDraft_${documentId}`;

/** 드래프트 autosave debounce (ms) */
export const COMMENT_DRAFT_DEBOUNCE_MS = 3000;

/**
 * 한 번에 노출할 행 수. v2 는 스레드 전체를 한 응답으로 주므로 서버 페이징이 아니라
 * "코멘트 더보기" 클라이언트 슬라이스 단위 — 추가 네트워크 호출 없음.
 */
export const COMMENT_PAGE_SIZE = 50;

/**
 * 스레드 조회/작성 응답 본문 — `GET|POST /api/v2/approval/{documentId}/comments` 의 envelope `data`.
 * meta·페이지네이션 없음. `commentCount` 는 결재 로그를 제외한 사용자 댓글 수.
 */
export type ApprovalCommentThread = {
  commentCount?: number;
  comments?: ApprovalCommentApiItem[];
};

/** 스레드 응답 행 — type 미명시(undefined)면 'comment' 로 간주. */
export type ApprovalCommentApiItem = {
  id: string;
  type?: 'comment' | 'log';
  action?: ApprovalLogAction;
  content: string;
  attachments?: RemoteFile[];
  mentions?: unknown[];
  createdBy: {
    passportId: string;
    userId?: string;
    employeeNumber?: string;
    name: string | { ko?: string; en?: string; zh?: string };
    positionName: string | { ko?: string; en?: string; zh?: string };
    departmentName?: string;
    divisionName?: string;
    profileImageUrl?: string | null;
    nickName?: string;
  };
  createdAt: string;
  updatedAt: string;
  // 공감(좋아요)은 v2 에서 `feedback` 중첩이 아니라 행 최상위로 내려온다. agree/disagree 는 없음.
  recommendCount?: number;
  isRecommend?: boolean;
  recommends?: (
    | string
    | {
        passportId?: string;
        employeeNumber?: string;
        name?: string | { ko?: string; en?: string; zh?: string };
        positionName?: string | { ko?: string; en?: string; zh?: string };
      }
  )[];
};
